import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/assets';
import { getContent, confirmJustice } from '../../api/content';
import Loading from './Loading';
import './JusticeModal.css';

interface JusticeModalProps {
  visible: boolean;
  onClose: () => void;
}

const JusticeModal: React.FC<JusticeModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation(['game']);
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [thumbTop, setThumbTop] = useState<string>('5.11rem'); // Initial top position from CSS
  const contentRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;

  // Constants for scrollbar (in rem)
  const INITIAL_THUMB_TOP_REM = 5.11;

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [visible]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getContent('migongdao');
      // Replace newline characters with <br/> tags for HTML rendering
      setContent((data.content || '').replace(/\n/g, '<br/>'));
      setTitle(data.title);
    } catch (error) {
      console.error('Failed to fetch justice content', error);
      setContent(t('justice.agreementContent').replace(/\n/g, '<br/>'));
      setTitle(t('justice.title'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      
    } catch (error) {
      console.error('Failed to confirm justice', error);
      toast.error(t('justice.confirmFailed', 'Failed to confirm'));
    } finally {
      onClose();
    }
  };

  const handleScroll = () => {
    if (!contentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const maxScroll = scrollHeight - clientHeight;
    
    if (maxScroll <= 0) {
        setThumbTop(`${INITIAL_THUMB_TOP_REM}rem`);
        return;
    }

    const scrollRatio = scrollTop / maxScroll;
    const maxThumbMovement = 3.23; // 5.14 - 1.91
    
    // Calculate new top
    const newTop = INITIAL_THUMB_TOP_REM + (scrollRatio * maxThumbMovement);
    setThumbTop(`${newTop}rem`);
  };

  return (
    <div className={`justice-modal-container ${visible ? 'visible' : ''}`}>
      <div className="justice-modal-backdrop" style={{ backgroundImage: getImg('popup-backdrop.png') }} onClick={onClose} />
      
      <div className="justice-content-wrapper">
        <div className="gg-main-frame">
          {/* Background Layer */}
          <div className="gg-background-layer" />
          
          {/* Content Panel Background */}
          <div className="gg-content-panel" style={{ backgroundImage: getImg('gg_content_panel.png') }} />
          
          {/* Close Button */}
          <div 
            className="gg-close-button" 
            style={{ backgroundImage: getImg('gg_close_button.png') }} 
            onClick={onClose}
          />
          
          {/* Title */}
          <span className="gg-title-text">{title || t('justice.title')}</span>
          
          {/* Confirm Button */}
          <div 
            className="gg-confirm-button" 
            style={{ backgroundImage: getImg('gg_button_primary.png') }}
            onClick={handleConfirm} // Or handle confirm action
          />
          <span className="gg-confirm-text">{t('common.confirm')}</span>
          
          {/* Cancel Button */}
          <div 
            className="gg-cancel-button" 
            style={{ backgroundImage: getImg('gg_button_primary.png') }}
            onClick={onClose}
          />
          <span className="gg-cancel-text">{t('common.cancel')}</span>
          
          {/* Agreement Box Background */}
          <div className="gg-agreement-box" style={{ backgroundImage: getImg('gg_agreement_box.png') }} />
          
          {/* Agreement Content */}
          <div 
            className="gg-agreement-content" 
            ref={contentRef}
            onScroll={handleScroll}
            style={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
          
          {/* Prompt Text */}
          <span className="gg-prompt-text" dangerouslySetInnerHTML={{ __html: `"${t('justice.prompt')}"` }} />
          
          {/* Custom Scrollbar */}
          <div 
            className="gg-scrollbar-track" 
            ref={trackRef}
            style={{ backgroundImage: getImg('gg_scrollbar_track.png') }} 
          />
          <div 
            className="gg-scrollbar-thumb" 
            style={{ 
              backgroundImage: getImg('gg_scrollbar_thumb.png'),
              top: thumbTop
            }} 
          />
          
          {/* Subtitle Label (NFT白名單) */}
          <span className="gg-subtitle-label">{t('justice.subtitle')}</span>
          
          {/* Title Decoration */}
          <div className="gg-title-decoration" style={{ backgroundImage: getImg('gg_title_decoration.png') }} />

          {loading && <Loading />}
        </div>
      </div>
    </div>
  );
};

export default JusticeModal;
